import { useCallback, useMemo, useEffect, useReducer } from 'react';

const SEND_OTP_COOLDOWN_SECONDS = 65;

interface UseSendOtpState {
  hasSendOtpError: boolean;
  hasSentInitialOtp: boolean;
  isSendingOtp: boolean;
  sendOtpCooldown: number | null;
}

const initState: UseSendOtpState = {
  hasSendOtpError: false,
  hasSentInitialOtp: false,
  isSendingOtp: false,
  sendOtpCooldown: null,
};

const COOLDOWN_TICK = 'COOLDOWN_TICK';

const SEND_OTP_PENDING = 'SEND_OTP_PENDING';

const SEND_OTP_FULFILLED = 'SEND_OTP_FULFILLED';

const SEND_OTP_FAILED = 'SEND_OTP_FAILED';

interface CooldownTickAction {
  type: typeof COOLDOWN_TICK;
}

interface SendOtpPendingAction {
  type: typeof SEND_OTP_PENDING;
}

interface SendOtpFulfilledAction {
  type: typeof SEND_OTP_FULFILLED;
}

interface SendOtpFailedAction {
  type: typeof SEND_OTP_FAILED;
}

type UseSendOtpStateAction =
  | CooldownTickAction
  | SendOtpFailedAction
  | SendOtpFulfilledAction
  | SendOtpPendingAction;

function reducer(
  state: UseSendOtpState,
  action: UseSendOtpStateAction
): UseSendOtpState {
  switch (action.type) {
    case COOLDOWN_TICK:
      if (state.sendOtpCooldown == null || state.sendOtpCooldown <= 1) {
        return { ...state, sendOtpCooldown: null };
      }

      return { ...state, sendOtpCooldown: state.sendOtpCooldown - 1 };
    case SEND_OTP_PENDING:
      return { ...state, isSendingOtp: true, hasSendOtpError: false };
    case SEND_OTP_FULFILLED:
      return {
        ...state,
        hasSentInitialOtp: true,
        isSendingOtp: false,
        sendOtpCooldown: SEND_OTP_COOLDOWN_SECONDS,
      };
    case SEND_OTP_FAILED:
      return { ...state, isSendingOtp: false, hasSendOtpError: true };
    default:
      return { ...state };
  }
}

interface UseSendOtpStateResult {
  onSendOtpFailed(): void;
  onSendOtpFulfilled(): void;
  onSendOtpPending(): void;
  state: UseSendOtpState;
}

function useSendOtpState(): UseSendOtpStateResult {
  const [state, dispatch] = useReducer(reducer, initState);
  const hasCooldown = state.sendOtpCooldown !== null;
  const onSendOtpPending = useCallback(
    (): void => dispatch({ type: SEND_OTP_PENDING }),
    []
  );
  const onSendOtpFulfilled = useCallback(
    (): void => dispatch({ type: SEND_OTP_FULFILLED }),
    []
  );
  const onSendOtpFailed = useCallback(
    (): void => dispatch({ type: SEND_OTP_FAILED }),
    []
  );

  useEffect(() => {
    let timerRef = window.setInterval(
      (): void => dispatch({ type: COOLDOWN_TICK }),
      1000
    );

    return () => {
      if (timerRef) {
        clearInterval(timerRef);
      }
    };
  }, [hasCooldown]);

  return useMemo(
    () => ({ onSendOtpFailed, onSendOtpFulfilled, onSendOtpPending, state }),
    [state, onSendOtpFailed, onSendOtpFulfilled, onSendOtpPending]
  );
}

export default useSendOtpState;
